import './app.css'
import { Context } from './services/boot'
import { Call, callEvents, CallReason } from '@closerplatform/sdk/index'
import { logger } from './utils/logger'
import { CallConnectionInfo, TrackInfo } from './services/abstractCallService'

export enum CallState {
  CONNECTING = "CONNECTING",
  RECORDING_CONNECTING = "RECORDING_CONNECTING",
  IN_PROGRESS = "IN_PROGRESS",
  ENDED = "ENDED"
}

export class App {
  private services: Context;
  private root: HTMLElement;

  // State
  private microphoneIsEnabled = true;
  private cameraIsEnabled = false;
  private call?: Call;
  private callState = CallState.CONNECTING;
  private callStartTimestamp?: number;
  private recordingEnabled = false;

  private localAudioTrackId?: string;
  private localVideoTrackId?: string;

  private fullVideo: "local-video" | "remote-video" | "remote-screen" = "remote-video";

  private microphones: MediaDeviceInfo[] = [];
  private selectedMicrophone?: string;
  private cameras: MediaDeviceInfo[] = [];
  private selectedCamera?: string;
  private selectingDeviceType?: "microphone" | "camera";

  private agentName = "";

  private callId?: string;
  private callToken?: string;
  private deviceId?: string;

  // DOM elements
  private localVideo!: HTMLVideoElement;
  private localAudio!: HTMLAudioElement;
  private remoteVideo!: HTMLVideoElement;
  private remoteAudio!: HTMLAudioElement;
  private localVideoWrapper!: HTMLElement;
  private remoteVideoWrapper!: HTMLElement;
  private agentNameEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private controlsEl!: HTMLElement;
  private toggleMicButton!: HTMLButtonElement;
  private selectMicButton!: HTMLButtonElement;
  private toggleCamButton!: HTMLButtonElement;
  private selectCamButton!: HTMLButtonElement;
  private hangupButton!: HTMLButtonElement;
  private deviceSelectEl!: HTMLElement;
  private deviceSelectorEl!: HTMLSelectElement;
  private cameraControlsEl!: HTMLElement;
  private microphoneControlsEl!: HTMLElement;

  // Timers
  private durationTimer?: number;

  constructor(root: HTMLElement, services: Context) {
    this.root = root;
    this.services = services;
  }

  async init() {
    this.createDOM();
    this.attachEventListeners();
    this.handleNavigation();

    this.setupServiceSubscriptions();

    window.addEventListener("popstate", () => this.handleNavigation());
  }

  private createDOM() {
    this.root.innerHTML = `
      <div class="media">
        <div class="details">
          <div class="agent-name" id="agent-name"></div>
          <div class="status" id="status">Connecting...</div>
        </div>
        <div class="video-wrapper full" id="remote-video-wrapper">
          <video id="remote-video" autoplay></video>
        </div>
        <div class="video-wrapper small" id="local-video-wrapper">
          <video id="local-video" autoplay></video>
        </div>
        <audio class="hidden" autoplay id="remote-audio"></audio>
        <audio class="hidden" muted id="local-audio"></audio>
      </div>
      <div class="bar">
        <div class="controls" id="controls">
          <div class="select" id="device-select" style="display: none;">
            <select id="device-selector"></select>
          </div>
          <div class="buttons">
            <div class="button-group" id="microphone-controls">
              <button id="toggle-microphone"></button>
              <div class="separator"></div>
              <button id="select-microphone"></button>
            </div>
            <div class="button-group" id="camera-controls">
              <button id="toggle-camera"></button>
              <div class="separator"></div>
              <button id="select-camera"></button>
            </div>
            <div class="button-group hangup">
              <button id="hangup">
                <div class="icon hangup"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Get references to DOM elements
    this.localVideo = document.getElementById('local-video') as HTMLVideoElement;
    this.localAudio = document.getElementById('local-audio') as HTMLAudioElement;
    this.remoteVideo = document.getElementById('remote-video') as HTMLVideoElement;
    this.remoteAudio = document.getElementById('remote-audio') as HTMLAudioElement;
    this.localVideoWrapper = document.getElementById('local-video-wrapper')!;
    this.remoteVideoWrapper = document.getElementById('remote-video-wrapper')!;
    this.agentNameEl = document.getElementById('agent-name')!;
    this.statusEl = document.getElementById('status')!;
    this.controlsEl = document.getElementById('controls')!;
    this.toggleMicButton = document.getElementById('toggle-microphone') as HTMLButtonElement;
    this.selectMicButton = document.getElementById('select-microphone') as HTMLButtonElement;
    this.toggleCamButton = document.getElementById('toggle-camera') as HTMLButtonElement;
    this.selectCamButton = document.getElementById('select-camera') as HTMLButtonElement;
    this.hangupButton = document.getElementById('hangup') as HTMLButtonElement;
    this.deviceSelectEl = document.getElementById('device-select')!;
    this.deviceSelectorEl = document.getElementById('device-selector') as HTMLSelectElement;
    this.cameraControlsEl = document.getElementById('camera-controls')!;
    this.microphoneControlsEl = document.getElementById('microphone-controls')!;
  }

  private attachEventListeners() {
    this.toggleMicButton.addEventListener('click', () => this.toggleMicrophone());
    this.selectMicButton.addEventListener('click', () => this.toggleMicrophoneSelecting());
    this.toggleCamButton.addEventListener('click', () => this.toggleCamera());
    this.selectCamButton.addEventListener('click', () => this.toggleCameraSelecting());
    this.hangupButton.addEventListener('click', () => this.hangup());

    this.localVideoWrapper.addEventListener('click', () => this.setFullVideo('local-video'));
    this.remoteVideoWrapper.addEventListener('click', () => this.setFullVideo('remote-video'));

    this.deviceSelectorEl.addEventListener('change', (ev) => {
      const target = ev.target as HTMLSelectElement;
      if (this.selectingDeviceType === 'microphone') {
        this.setSelectedMicrophone(target.value);
      } else if (this.selectingDeviceType === 'camera') {
        this.setSelectedCamera(target.value);
      }
    });
  }

  private async enumerateDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.microphones = devices.filter((device) => device.kind === "audioinput");
    this.selectedMicrophone = this.microphones[0]?.deviceId;
    this.cameras = devices.filter((device) => device.kind === "videoinput");
    this.selectedCamera = this.cameras[0]?.deviceId;
  }

  private setupServiceSubscriptions() {
    this.services.callService.ended$.subscribe(() => this.handleEnded());
    this.services.callService.trackReceived$.subscribe((trackInfo) => this.handleRemoteTrackReceived(trackInfo));
    this.services.callService.connected$.subscribe((connectionInfo) => this.handleConnected(connectionInfo));
    this.services.callService.answered$.subscribe(() => this.handleAnswered());
  }

  private handleNavigation() {
    const args = new URLSearchParams(window.location.hash.substring(1));
    this.callId = args.get("callId") || undefined;
    this.callToken = args.get("callToken") || undefined;
    this.deviceId = this.deviceId || args.get("deviceId") || undefined;

    if (this.callId && this.callToken && this.deviceId) {
      this.enrollAndConnect();
    }
  }

  private async enrollAndConnect() {
    const audioTrack = await this.services.mediaService.getMicrophoneTrack();
    await this.enumerateDevices();

    if (!this.callId || !this.callToken || !this.deviceId) return;

    const { userId } = await this.services.bffService.rpc.enroll.mutate({
      callId: this.callId,
      callToken: this.callToken,
      deviceId: this.deviceId
    });

    this.services.artichokeService.connect(userId);
    this.call = await this.services.artichokeService.getCall(this.callId);
    const agentId = this.call.invitees[0] || this.call.users.filter((id) => id != userId)[0];

    this.recordingEnabled = this.call.recordingEnabled;

    if (!agentId) {
      return this.handleEnded();
      // alert("No agent in the call");
      // window.close();
    }

    if (audioTrack) {
      this.localAudioTrackId = audioTrack.id;
      this.services.callService.connect(this.callId, userId, agentId, audioTrack);
    }

    this.services.usersApi.getLimitedAgentProfile(agentId).then((agentProfile) => {
      this.agentName = [agentProfile.firstName, agentProfile.lastName].join(" ");
      this.render();
    });
  }

  private handleEnded() {
    logger.debug("CallStore: call ended");
    this.callState = CallState.ENDED;
    this.stopMediaDevices();
    this.render();
  }

  private handleAnswered() {
    if (!this.recordingEnabled) {
      this.callState = CallState.IN_PROGRESS;
      this.callStartTimestamp = Date.now();
      this.startDurationTimer();
      this.render();
    }
  }

  private handleConnected(connectionInfo: CallConnectionInfo) {
    if (connectionInfo.type === callEvents.ConnectionType.Subscriber && this.recordingEnabled) {
      logger.info("CallStore: Subscriber connection established");
      this.callState = CallState.IN_PROGRESS;
      this.callStartTimestamp = Date.now();
      this.startDurationTimer();
      this.render();
    }
  }

  private handleRemoteTrackReceived(trackInfo: TrackInfo) {
    logger.debug("CallStore: received new track event", trackInfo);
    if (this.callState !== "IN_PROGRESS") {
      this.callState = CallState.IN_PROGRESS;
      this.callStartTimestamp = Date.now();
      this.startDurationTimer();
      this.render();
    }

    if (trackInfo.track && trackInfo.kind === "audio" && this.remoteAudio) {
      this.remoteAudio.srcObject = new MediaStream([trackInfo.track]);
      // this.remoteAudioTrackId = this.services.mediaService.setTrack(trackInfo.track).id;
    }

    if (trackInfo.track && trackInfo.kind === "video" && this.remoteVideo) {
      this.remoteVideo.srcObject = new MediaStream([trackInfo.track]);
      // this.remoteVideoTrackId = this.services.mediaService.setTrack(trackInfo.track).id;
    }

    if (trackInfo.enabled !== undefined) {
      const mediaTrack = this.services.mediaService.getTrack(trackInfo.track?.id);
      if (mediaTrack) {
        mediaTrack.enabled = trackInfo.enabled;
      }
    }
  }

  private stopMediaDevices() {
    this.services.mediaService.stopAllTracks()
  }

  private async toggleMicrophone() {
    this.microphoneIsEnabled = !this.microphoneIsEnabled;
    await this.updateMicrophoneState();
    this.render();
  }

  private async updateMicrophoneState() {
    if (this.microphoneIsEnabled) {
      const track = await this.services.mediaService.getMicrophoneTrack(this.selectedMicrophone);

      this.localAudioTrackId = track?.id;
      if (track && this.localAudio) {
        this.localAudio.srcObject = new MediaStream([track]);
        track.enabled = true;
        this.services.callService.setAudioStream({ enabled: true, track });
      }
    } else {
      const track = this.services.mediaService.getTrack(this.localAudioTrackId);
      if (track) {
        track.enabled = false;
      }
      if (this.localAudio) {
        this.localAudio.srcObject = null;
      }
    }
  }

  private toggleMicrophoneSelecting() {
    this.selectingDeviceType = this.selectingDeviceType !== "microphone" ? "microphone" : undefined;
    this.render();
    if (this.selectingDeviceType === "microphone") {
      this.populateDeviceSelector(this.microphones);
    }
  }

  private async toggleCamera() {
    this.cameraIsEnabled = !this.cameraIsEnabled;
    await this.updateCameraState();
    this.render();
  }

  private async updateCameraState() {
    if (this.cameraIsEnabled) {
      const track = await this.services.mediaService.getCameraTrack(this.selectedCamera);
      await this.enumerateDevices();
      this.localVideoTrackId = track?.id;
      if (track && this.localVideo) {
        this.localVideo.srcObject = new MediaStream([track]);
        track.enabled = true;
        this.services.callService.setVideoStream({ enabled: true, track, content: "camera" });
      }
    } else {
      const track = this.services.mediaService.getTrack(this.localVideoTrackId);
      if (track) {
        track.enabled = false;
      }
      if (this.localVideo) {
        this.localVideo.srcObject = null;
      }
    }
  }

  private toggleCameraSelecting() {
    this.selectingDeviceType = this.selectingDeviceType !== "camera" ? "camera" : undefined;
    this.render();
    if (this.selectingDeviceType === "camera") {
      this.populateDeviceSelector(this.cameras);
    }
  }

  private populateDeviceSelector(devices: MediaDeviceInfo[]) {
    this.deviceSelectorEl.innerHTML = '';
    devices.forEach((device) => {
      const option = document.createElement('option');
      option.value = device.deviceId;
      option.textContent = device.label.split("(")[0];
      this.deviceSelectorEl.appendChild(option);
    });
  }

  private setSelectedMicrophone(deviceId: string) {
    this.selectedMicrophone = deviceId;
    this.updateMicrophoneState();
  }

  private setSelectedCamera(deviceId: string) {
    this.selectedCamera = deviceId;
    this.updateCameraState();
  }

  private setFullVideo(video: "local-video" | "remote-video" | "remote-screen") {
    this.fullVideo = video;
    this.render();
  }

  private hangup() {
    this.call?.leave(CallReason.Hangup);
    this.callState = CallState.ENDED;
    this.stopMediaDevices();
    this.render();
  }

  private startDurationTimer() {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
    }
    this.durationTimer = window.setInterval(() => this.updateDuration(), 500);
  }

  private updateDuration() {
    if (this.callStartTimestamp && this.callState === CallState.IN_PROGRESS) {
      const duration = Date.now() - this.callStartTimestamp;
      const m = Math.floor(duration / 60 / 1000);
      const s = Math.floor(duration / 1000) % 60;
      const formatted = [m, s].map((v) => v < 10 ? `0${v}` : v).join(":");
      this.statusEl.textContent = formatted;
    }
  }

  private icon(type: "microphone" | "camera" | "ribbon" | "ribbon-rotated", crossed = false) {
    const className = ["icon", type].join(" ");
    return `<div class="${className}"></div>` + (crossed ? `<div class="crossed"></div>` : "");
  }

  private render() {
    // Update agent name
    this.agentNameEl.textContent = this.agentName;

    // Update status
    if (this.callState === CallState.CONNECTING || this.callState === CallState.RECORDING_CONNECTING) {
      this.statusEl.textContent = "Connecting...";
    } else if (this.callState === CallState.ENDED) {
      this.statusEl.textContent = "Call ended";
      if (this.durationTimer) {
        clearInterval(this.durationTimer);
      }
    }

    // Update video visibility
    const videoIsEnabled = !this.recordingEnabled;
    this.localVideoWrapper.style.display = videoIsEnabled ? 'flex' : 'none';
    this.remoteVideoWrapper.style.display = videoIsEnabled ? 'flex' : 'none';

    // Update video sizes
    if (this.fullVideo === 'local-video') {
      this.localVideoWrapper.className = 'video-wrapper full';
      this.remoteVideoWrapper.className = 'video-wrapper small';
    } else {
      this.localVideoWrapper.className = 'video-wrapper small';
      this.remoteVideoWrapper.className = 'video-wrapper full';
    }

    // Update controls visibility
    if (this.callState === CallState.ENDED) {
      this.controlsEl.style.display = 'none';
    } else {
      this.controlsEl.style.display = 'flex';
    }

    // Update buttons
    this.microphoneControlsEl.className = this.microphoneIsEnabled ? "button-group enabled" : "button-group disabled";
    this.cameraControlsEl.className = this.cameraIsEnabled ? "button-group enabled" : "button-group disabled";
    this.toggleMicButton.innerHTML = this.icon("microphone", !this.microphoneIsEnabled);
    this.selectMicButton.innerHTML = this.icon(this.selectingDeviceType === "microphone" ? "ribbon" : "ribbon-rotated");
    this.toggleCamButton.innerHTML = this.icon("camera", !this.cameraIsEnabled);
    this.selectCamButton.innerHTML = this.icon(this.selectingDeviceType === "camera" ? "ribbon" : "ribbon-rotated");
    if (this.microphones.length > 1) {
      this.selectMicButton.style.display = "initial";
    } else {
      this.selectMicButton.style.display = "none";
    }
    if (this.cameras.length > 1) {
      this.selectCamButton.style.display = "initial";
    } else {
      this.selectCamButton.style.display = "none";
    }

    // Update device selector visibility
    if (this.selectingDeviceType) {
      this.deviceSelectEl.style.display = 'block';
    } else {
      this.deviceSelectEl.style.display = 'none';
    }

    // Update camera controls visibility
    if (videoIsEnabled) {
      this.cameraControlsEl.style.display = 'flex';
    } else {
      this.cameraControlsEl.style.display = 'none';
    }
  }

  destroy() {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
    }
    this.stopMediaDevices();
  }
}
